import type { ReplyWindow } from "@school-sales-ai/shared";

/**
 * How to present WhatsApp's 24-hour reply window.
 *
 * One rule for both surfaces that show it — the composer in the lead detail and
 * the row in the Buzón — so the two can never tell the user different things
 * about the same lead.
 *
 * `closed` is derived here rather than sent by the server, which is what lets
 * the label stay truthful between polls: the server says when the window
 * expires, this says what that means right now.
 */

/** Below this, the window is worth warning about rather than just reporting. */
const CLOSING_SOON_MS = 2 * 60 * 60 * 1000;

export type ReplyWindowState =
  /** Transport has no window (Baileys). Show nothing; sending always works. */
  | "none"
  /** The prospect has never written on WhatsApp. Cannot send. */
  | "never"
  | "open"
  | "closing"
  | "closed";

export type ReplyWindowView = {
  state: ReplyWindowState;
  /** Milliseconds left, or null when the notion does not apply. */
  remainingMs: number | null;
  canSend: boolean;
};

export function describeReplyWindow(
  window: ReplyWindow | null | undefined,
  now: Date = new Date(),
): ReplyWindowView {
  // Undefined while the lead detail is still loading — treat as "no opinion yet"
  // rather than blocking the composer, which would flash a false refusal.
  if (!window || window.kind === "none") {
    return { state: "none", remainingMs: null, canSend: true };
  }
  if (window.kind === "never") {
    return { state: "never", remainingMs: null, canSend: false };
  }

  const remainingMs = new Date(window.expiresAt).getTime() - now.getTime();
  if (remainingMs <= 0) return { state: "closed", remainingMs: 0, canSend: false };
  if (remainingMs <= CLOSING_SOON_MS) {
    return { state: "closing", remainingMs, canSend: true };
  }
  return { state: "open", remainingMs, canSend: true };
}

/**
 * "6 h 12 min", "48 min", "1 min".
 *
 * `formatElapsed` cannot be reused: it takes whole hours and rounds minutes
 * away, and the last two hours of this window are exactly when the number
 * matters most.
 */
export function formatRemaining(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  // Instagram's window is 7 days: "143 h" reads as a number, "5 d 23 h" as a time.
  // WhatsApp's never reaches a day, so its labels are unchanged.
  if (totalMinutes >= 24 * 60) {
    const days = Math.floor(totalMinutes / (24 * 60));
    const restHours = Math.floor((totalMinutes % (24 * 60)) / 60);
    return restHours === 0 ? `${days} d` : `${days} d ${restHours} h`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${Math.max(1, minutes)} min`;
  if (minutes === 0) return `${hours} h`;
  return `${hours} h ${minutes} min`;
}
